import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import { urlFor } from '../lib/imageBuilder';

const ANNOTATION_GAP_PX = 80; // distance from image right edge to annotation card
const ANNOTATION_WIDTH_PX = 300;
const MOBILE_BREAKPOINT_PX = 900;

// Floating annotation card — positioned in viewport space, tracking the active hotspot.
const FloatingAnnotation = ({
  hotspotRefMap,
  activeHotspotIndex,
  imageWrapperRef,
  annotation,
  activeKey,
  hotspotCount,
  onPrev,
  onNext,
}) => {
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const hotspotEl = hotspotRefMap.current?.[activeHotspotIndex];
    const imageWrapperEl = imageWrapperRef.current;
    if (!hotspotEl || !imageWrapperEl) {
      setPos(null);
      return;
    }
    const update = () => {
      const h = hotspotEl.getBoundingClientRect();
      const w = imageWrapperEl.getBoundingClientRect();
      // Anchor annotation just right of the image, vertically aligned with the dot
      const left = w.right + ANNOTATION_GAP_PX;
      const top = h.top + h.height / 2;
      setPos({ left, top });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [hotspotRefMap, activeHotspotIndex, imageWrapperRef, activeKey]);

  if (!pos) return null;

  return (
    <motion.div
      ref={cardRef}
      key={activeKey}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, delay: 0.25, ease: 'easeOut' }}
      className="fixed pointer-events-auto"
      style={{
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        transform: 'translateY(-50%)',
        width: `${ANNOTATION_WIDTH_PX}px`,
        zIndex: 46,
      }}
      data-testid="annotation-floating"
    >
      <p
        className="font-body text-base text-foreground-primary leading-relaxed"
        data-testid="annotation-text"
      >
        {annotation}
      </p>
      {/* Hotspot prev/next nav — only shown when there's more than one on this screen */}
      {hotspotCount > 1 && (
        <div className="mt-4 flex items-center justify-end gap-3">
          <span className="font-body text-xs text-foreground-secondary tracking-wider">
            {activeHotspotIndex + 1} / {hotspotCount}
          </span>
          <button
            onClick={onPrev}
            className="w-7 h-7 rounded-full border border-border-subtle text-foreground-secondary hover:text-foreground-primary hover:border-accent flex items-center justify-center transition-colors"
            aria-label="Previous hotspot"
            data-testid="annotation-prev-hotspot"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
          </button>
          <button
            onClick={onNext}
            className="w-7 h-7 rounded-full border border-border-subtle text-foreground-secondary hover:text-foreground-primary hover:border-accent flex items-center justify-center transition-colors"
            aria-label="Next hotspot"
            data-testid="annotation-next-hotspot"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      )}
    </motion.div>
  );
};

// A single pulsing "hot dot" placed at relative (x, y) on its parent image.
// Three states: unvisited (white + pulse), visited (filled accent, no pulse), active (scaled up).
const Hotspot = React.forwardRef(({ x, y, isActive, isVisited, onClick }, ref) => {
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  return (
    <button
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="absolute flex items-center justify-center w-7 h-7 -translate-x-1/2 -translate-y-1/2 group"
      style={{ left: `${x}%`, top: `${y}%` }}
      aria-label={isVisited ? 'Show annotation (visited)' : 'Show annotation'}
      data-testid={isVisited ? 'hotspot-visited' : 'hotspot'}
    >
      {/* Pulse ring — only when unvisited */}
      {!isVisited && (
        <span
          className="absolute w-6 h-6 rounded-full bg-accent/65 animate-pulse-soft"
          aria-hidden="true"
        />
      )}
      {/* Inner dot — solid accent fill for stronger visibility; muted gray-green when visited */}
      <span
        className={`relative block w-3.5 h-3.5 rounded-full border-2 border-white shadow-md transition-all duration-200 ${
          isVisited ? 'bg-foreground-secondary' : 'bg-accent'
        } ${
          isActive ? 'scale-125 shadow-lg' : 'group-hover:scale-110'
        }`}
      />
    </button>
  );
});
Hotspot.displayName = 'Hotspot';

// SVG arrow drawn from the active hotspot to the floating annotation card.
// Reads refs inside useLayoutEffect to avoid touching `.current` during render.
const ArrowOverlay = ({ hotspotRefMap, activeHotspotIndex, imageWrapperRef, activeKey }) => {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    const hotspotEl = hotspotRefMap.current?.[activeHotspotIndex];
    const imageWrapperEl = imageWrapperRef.current;
    if (!hotspotEl || !imageWrapperEl) {
      setCoords(null);
      return;
    }
    const update = () => {
      const h = hotspotEl.getBoundingClientRect();
      const w = imageWrapperEl.getBoundingClientRect();
      const startX = h.left + h.width / 2;
      const startY = h.top + h.height / 2;
      // Arrow ends just before the annotation card (which sits at imageRight + GAP)
      const endX = w.right + ANNOTATION_GAP_PX - 12;
      const endY = startY;
      // Slight downward curve via a control point
      const midX = (startX + endX) / 2;
      const midY = startY - 14;
      setCoords({
        d: `M ${startX} ${startY} Q ${midX} ${midY}, ${endX} ${endY}`,
        endX,
        endY,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [hotspotRefMap, activeHotspotIndex, imageWrapperRef, activeKey]);

  if (!coords) return null;

  return (
    <svg
      className="fixed inset-0 w-full h-full pointer-events-none text-accent"
      style={{ zIndex: 45 }}
      data-testid="hotspot-arrow"
    >
      <motion.path
        key={activeKey}
        d={coords.d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 0.9 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      />
      <motion.polyline
        key={`tip-${activeKey}`}
        points={`${coords.endX - 7},${coords.endY - 4} ${coords.endX},${coords.endY} ${coords.endX - 7},${coords.endY + 4}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.9 }}
        transition={{ duration: 0.2, delay: 0.3 }}
      />
    </svg>
  );
};

export const AnnotatedScreensModal = ({ screens, initialIndex = 0, isOpen, onClose }) => {
  const [currentScreenIndex, setCurrentScreenIndex] = useState(initialIndex);
  const [activeHotspotIndex, setActiveHotspotIndex] = useState(null);
  const [offscreen, setOffscreen] = useState({ above: 0, below: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [, forceRender] = useState(0); // used to bump arrow recompute on hotspot mount
  // Visited hotspot tracking — keys are "screenIdx-hotspotIdx". Resets when modal unmounts.
  const [visited, setVisited] = useState(() => new Set());

  const scrollRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const hotspotRefs = useRef({});

  const currentScreen = screens?.[currentScreenIndex];
  const hotspots = currentScreen?.hotspots || [];

  // Centralised screen-change handler: resets hotspot state + scroll position.
  const changeScreen = useCallback((newIdx) => {
    setCurrentScreenIndex(newIdx);
    setActiveHotspotIndex(null);
    hotspotRefs.current = {};
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  // Hotspot click: select it AND mark as visited for this session.
  // When `scroll` is true, smoothly center the target hotspot in the scroll container —
  // used by the prev/next arrows so the user always sees where the new annotation belongs.
  const handleHotspotClick = useCallback(
    (idx, { scroll = false } = {}) => {
      setActiveHotspotIndex(idx);
      setVisited((prev) => {
        const next = new Set(prev);
        next.add(`${currentScreenIndex}-${idx}`);
        return next;
      });
      if (scroll) {
        // Defer one frame so the active state is committed before measuring.
        requestAnimationFrame(() => {
          const node = hotspotRefs.current?.[idx];
          if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    },
    [currentScreenIndex],
  );

  // Mobile detection (also used for "no room for floating annotation" fallback)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT_PX);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Lock background page scroll while modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (activeHotspotIndex !== null) setActiveHotspotIndex(null);
        else onClose();
      } else if (e.key === 'ArrowLeft' && screens.length > 1) {
        changeScreen(currentScreenIndex === 0 ? screens.length - 1 : currentScreenIndex - 1);
      } else if (e.key === 'ArrowRight' && screens.length > 1) {
        changeScreen(currentScreenIndex === screens.length - 1 ? 0 : currentScreenIndex + 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, activeHotspotIndex, screens?.length, onClose, changeScreen, currentScreenIndex]);

  // Compute off-screen hotspot counts based on the scroll container's viewport.
  const recomputeOffscreen = useCallback(() => {
    if (!scrollRef.current) return;
    const containerRect = scrollRef.current.getBoundingClientRect();
    let above = 0;
    let below = 0;
    Object.values(hotspotRefs.current).forEach((node) => {
      if (!node) return;
      const r = node.getBoundingClientRect();
      const center = r.top + r.height / 2;
      if (center < containerRect.top + 24) above += 1;
      else if (center > containerRect.bottom - 24) below += 1;
    });
    setOffscreen({ above, below });
  }, []);

  useEffect(() => {
    recomputeOffscreen();
  }, [recomputeOffscreen, currentScreenIndex, hotspots.length]);

  const scrollToNearest = (direction) => {
    if (!scrollRef.current) return;
    const containerRect = scrollRef.current.getBoundingClientRect();
    let target = null;
    let targetMetric = direction === 'above' ? -Infinity : Infinity;

    Object.values(hotspotRefs.current).forEach((node) => {
      if (!node) return;
      const r = node.getBoundingClientRect();
      const center = r.top + r.height / 2;
      if (direction === 'above' && center < containerRect.top + 24) {
        if (center > targetMetric) {
          targetMetric = center;
          target = node;
        }
      } else if (direction === 'below' && center > containerRect.bottom - 24) {
        if (center < targetMetric) {
          targetMetric = center;
          target = node;
        }
      }
    });

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (!isOpen || !screens || screens.length === 0) return null;

  const imageUrl = currentScreen?.image?.asset
    ? urlFor(currentScreen.image).width(1000).url()
    : null;

  const activeHotspot = activeHotspotIndex !== null ? hotspots[activeHotspotIndex] : null;

  return (
    <motion.div
      className="fixed inset-0 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      data-testid="annotated-screens-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-white/80 backdrop-blur-md"
        onClick={onClose}
        data-testid="modal-backdrop"
      />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-40 w-10 h-10 rounded-full bg-white shadow-md hover:bg-foreground-primary hover:text-white flex items-center justify-center transition-colors"
        aria-label="Close"
        data-testid="modal-close"
      >
        <X className="w-5 h-5" strokeWidth={1.75} />
      </button>

      {/* Layout: full-width image area + (floating annotation desktop | bottom sheet mobile) */}
      <div className="absolute inset-0">
        {/* Title header — pinned at the top with solid bg so the scrolling image never overlaps it */}
        <div
          className="absolute top-0 left-0 right-0 z-30 pt-6 pb-4 px-20 bg-background-primary border-b border-border-subtle/70 pointer-events-none"
          data-testid="modal-title-header"
        >
          <div className="text-center">
            <p className="font-body text-xs text-accent-text uppercase tracking-wider">
              Screen {currentScreenIndex + 1} of {screens.length}
            </p>
            {currentScreen?.caption && (
              <h3 className="font-heading text-2xl text-foreground-primary mt-1 leading-tight">
                {currentScreen.caption}
              </h3>
            )}
          </div>
        </div>

        {/* Off-screen pill — above */}
        <AnimatePresence>
          {offscreen.above > 0 && (
            <motion.button
              key="pill-above"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              onClick={() => scrollToNearest('above')}
              className="absolute top-24 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-primary text-white text-xs font-body shadow-md hover:bg-foreground-primary/90 transition-colors"
              data-testid="offscreen-pill-above"
            >
              <ArrowUp className="w-3.5 h-3.5" strokeWidth={2} />
              {offscreen.above} hotspot{offscreen.above !== 1 ? 's' : ''} above
            </motion.button>
          )}
        </AnimatePresence>

        {/* Off-screen pill — below */}
        <AnimatePresence>
          {offscreen.below > 0 && (
            <motion.button
              key="pill-below"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              onClick={() => scrollToNearest('below')}
              className="absolute bottom-32 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-primary text-white text-xs font-body shadow-md hover:bg-foreground-primary/90 transition-colors"
              data-testid="offscreen-pill-below"
            >
              <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} />
              {offscreen.below} hotspot{offscreen.below !== 1 ? 's' : ''} below
            </motion.button>
          )}
        </AnimatePresence>

        {/* Screen navigation arrows (desktop) */}
        {screens.length > 1 && !isMobile && (
          <>
            <button
              onClick={() =>
                changeScreen(currentScreenIndex === 0 ? screens.length - 1 : currentScreenIndex - 1)
              }
              className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white shadow-md hover:bg-foreground-primary hover:text-white flex items-center justify-center transition-colors"
              aria-label="Previous screen"
              data-testid="modal-prev-screen"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() =>
                changeScreen(currentScreenIndex === screens.length - 1 ? 0 : currentScreenIndex + 1)
              }
              className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white shadow-md hover:bg-foreground-primary hover:text-white flex items-center justify-center transition-colors"
              aria-label="Next screen"
              data-testid="modal-next-screen"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Scrollable image container. Clicking the whitespace (not the image / hotspots) closes the modal.
            `items-start` is critical: prevents the flex wrapper from stretching to the modal's
            full height, so the wrapper sizes to the actual image height and hot-dot percentages
            resolve correctly (and the "below" off-screen pill works). */}
        <div
          ref={scrollRef}
          onScroll={recomputeOffscreen}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          className="h-full overflow-y-auto flex justify-center items-start"
          style={{ paddingTop: '7rem', paddingBottom: '7.5rem' }}
        >
          <div
            className="relative mx-4"
            ref={imageWrapperRef}
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            {imageUrl && (
              <img
                src={imageUrl}
                alt={
                  currentScreen?.image?.alt ||
                  currentScreen?.caption ||
                  `Screen ${currentScreenIndex + 1}`
                }
                className="block rounded-2xl shadow-2xl"
                style={{
                  maxWidth: isMobile ? '88vw' : '380px',
                  width: '100%',
                  height: 'auto',
                }}
                onLoad={() => {
                  recomputeOffscreen();
                  forceRender((n) => n + 1);
                }}
              />
            )}

            {/* Hotspots */}
            {hotspots.map((hs, idx) => (
              <Hotspot
                key={hs._key || idx}
                ref={(el) => {
                  if (el) hotspotRefs.current[idx] = el;
                  else delete hotspotRefs.current[idx];
                }}
                x={hs.x}
                y={hs.y}
                isActive={activeHotspotIndex === idx}
                isVisited={visited.has(`${currentScreenIndex}-${idx}`)}
                onClick={() => handleHotspotClick(idx)}
              />
            ))}
          </div>
        </div>

        {/* Mobile bottom sheet */}
        {isMobile && (
          <AnimatePresence>
            {activeHotspot && (
              <motion.div
                key="mobile-sheet"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
                className="absolute bottom-0 left-0 right-0 z-30 bg-background-primary rounded-t-2xl shadow-2xl p-6 pb-8"
                data-testid="annotation-sheet"
              >
                <div className="w-12 h-1 rounded-full bg-border-subtle mx-auto mb-4" />
                <p className="font-body text-xs text-accent-text uppercase tracking-wider mb-2">
                  Detail {activeHotspotIndex + 1}
                </p>
                <p
                  className="font-body text-foreground-primary leading-relaxed mb-4"
                  data-testid="annotation-text"
                >
                  {activeHotspot.annotation}
                </p>
                <button
                  onClick={() => setActiveHotspotIndex(null)}
                  className="text-sm text-foreground-secondary underline"
                >
                  Close
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Thumbnail strip */}
        {screens.length > 1 && (
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-stretch bg-white/95 backdrop-blur-sm rounded-xl shadow-md border border-border-subtle p-2.5 gap-2.5"
            data-testid="thumb-strip"
          >
            {screens.map((screen, i) => (
              <div
                key={i}
                className={
                  i > 0
                    ? 'pl-2.5 border-l border-border-subtle flex items-center'
                    : 'flex items-center'
                }
              >
                <button
                  onClick={() => changeScreen(i)}
                  className={`w-11 h-16 rounded overflow-hidden transition-all ${
                    i === currentScreenIndex
                      ? 'ring-2 ring-accent opacity-100'
                      : 'opacity-55 hover:opacity-100'
                  }`}
                  aria-label={`Go to screen ${i + 1}`}
                  data-testid={`thumb-${i}`}
                >
                  {screen.image?.asset && (
                  <img
                    src={urlFor(screen.image).width(160).url()}
                    alt=""
                    className="w-full h-full object-cover object-top"
                  />
                )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Floating annotation (desktop only) — appears next to the active hotspot */}
      {!isMobile && activeHotspot && (
        <AnimatePresence mode="wait">
          <FloatingAnnotation
            key={`annotation-${currentScreenIndex}-${activeHotspotIndex}`}
            hotspotRefMap={hotspotRefs}
            activeHotspotIndex={activeHotspotIndex}
            imageWrapperRef={imageWrapperRef}
            annotation={activeHotspot.annotation}
            activeKey={`${currentScreenIndex}-${activeHotspotIndex}`}
            hotspotCount={hotspots.length}
            onPrev={() => {
              const prevIdx =
                activeHotspotIndex === 0 ? hotspots.length - 1 : activeHotspotIndex - 1;
              handleHotspotClick(prevIdx, { scroll: true });
            }}
            onNext={() => {
              const nextIdx =
                activeHotspotIndex === hotspots.length - 1 ? 0 : activeHotspotIndex + 1;
              handleHotspotClick(nextIdx, { scroll: true });
            }}
          />
        </AnimatePresence>
      )}

      {/* SVG arrow overlay (desktop only) */}
      {!isMobile && activeHotspotIndex !== null && (
        <ArrowOverlay
          hotspotRefMap={hotspotRefs}
          activeHotspotIndex={activeHotspotIndex}
          imageWrapperRef={imageWrapperRef}
          activeKey={`${currentScreenIndex}-${activeHotspotIndex}`}
        />
      )}
    </motion.div>
  );
};

export default AnnotatedScreensModal;
